import { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, XCircle, Clock, ChevronUp } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { useTranslation } from 'react-i18next';
import type { JobHistoryItem, JobStatus, JobType } from '@/types/api/document-training';
import { GroupedFieldsView } from './grouped-fields-view';

interface JobHistorySectionProps {
    trainingDocId: string;
    jobs: JobHistoryItem[];
    isLoading?: boolean;
    isError?: boolean;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    onJobClick?: (job: JobHistoryItem) => void;
}

export function JobHistorySection({
    trainingDocId,
    jobs = [],
    isLoading,
    isError,
    isCollapsed = false,
    onToggleCollapse,
    onJobClick
}: JobHistorySectionProps) {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

    // Ensure jobs is always an array
    const safeJobs = Array.isArray(jobs) ? jobs : [];

    const getStatusBadge = (status: JobStatus) => {
        switch (status) {
            case 'completed':
                return (
                    <Badge color="success" size="sm">
                        <CheckCircle className="size-3 mr-1" />
                        {t('training.detail.jobHistory.status.completed', { defaultValue: 'Completed' })}
                    </Badge>
                );
            case 'failed':
                return (
                    <Badge color="error" size="sm">
                        <XCircle className="size-3 mr-1" />
                        {t('training.detail.jobHistory.status.failed', { defaultValue: 'Failed' })}
                    </Badge>
                );
            case 'processing':
                return (
                    <Badge color="warning" size="sm">
                        <div className="size-3 mr-1 border-2 border-warning border-t-transparent rounded-full animate-spin" />
                        {t('training.detail.jobHistory.status.processing', { defaultValue: 'Processing' })}
                    </Badge>
                );
            case 'pending':
            default:
                return (
                    <Badge color="gray" size="sm">
                        <Clock className="size-3 mr-1" />
                        {t('training.detail.jobHistory.status.pending', { defaultValue: 'Pending' })}
                    </Badge>
                );
        }
    };

    const getJobTypeBadge = (jobType: JobType) => {
        switch (jobType) {
            case 'parsing':
            case 'extraction':
                return (
                    <Badge color="blue" size="sm">
                        {t('training.detail.jobHistory.type.parsing', { defaultValue: 'Extraction' })}
                    </Badge>
                );
            case 'classification':
                return (
                    <Badge color="purple" size="sm">
                        {t('training.detail.jobHistory.type.classification', { defaultValue: 'Classification' })}
                    </Badge>
                );
            case 'ocr':
                return (
                    <Badge color="orange" size="sm">
                        OCR
                    </Badge>
                );
            default:
                return (
                    <Badge color="gray" size="sm">
                        {jobType}
                    </Badge>
                );
        }
    };

    const formatDuration = (ms: number | null | undefined) => {
        if (!ms) return '-';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('it-IT', {
            dateStyle: 'short',
            timeStyle: 'short',
        });
    };

    const handleRowClick = (job: JobHistoryItem) => {
        if (expandedJobId === job.job_id) {
            setExpandedJobId(null);
        } else {
            setExpandedJobId(job.job_id);
        }
        onJobClick?.(job);
    };

    // Header with collapse toggle
    const renderHeader = () => (
        <button
            onClick={onToggleCollapse}
            className="flex items-center justify-between w-full cursor-pointer hover:opacity-80 transition-opacity"
        >
            <h3 className="text-lg font-semibold text-primary">
                {t('training.detail.jobHistory.title', { defaultValue: 'Job History' })}
                {safeJobs.length > 0 && ` (${safeJobs.length})`}
            </h3>
            {isCollapsed ? (
                <ChevronDown className="size-5 text-tertiary" />
            ) : (
                <ChevronUp className="size-5 text-tertiary" />
            )}
        </button>
    );

    if (isLoading) {
        return (
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                {renderHeader()}
                {!isCollapsed && (
                    <div className="flex items-center justify-center py-8 mt-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand"></div>
                    </div>
                )}
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                {renderHeader()}
                {!isCollapsed && (
                    <div className="text-center py-8 mt-4">
                        <p className="text-tertiary">
                            {t('training.detail.jobHistory.loadError', { defaultValue: 'Error loading job history.' })}
                        </p>
                    </div>
                )}
            </div>
        );
    }

    if (safeJobs.length === 0) {
        return (
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                {renderHeader()}
                {!isCollapsed && (
                    <div className="text-center py-8 mt-4">
                        <p className="text-tertiary">
                            {t('training.detail.jobHistory.noJobs', { defaultValue: 'No jobs yet. Run an extraction or classification to see job history.' })}
                        </p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            {renderHeader()}

            {!isCollapsed && (
                <div className="overflow-x-auto mt-4">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-secondary bg-secondary">
                                <th className="py-2 px-3 text-left text-xs font-semibold text-quaternary w-10"></th>
                                <th className="py-2 px-3 text-left text-xs font-semibold text-quaternary">
                                    {t('training.detail.jobHistory.headers.type', { defaultValue: 'Type' })}
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-semibold text-quaternary">
                                    {t('training.detail.jobHistory.headers.strategy', { defaultValue: 'Strategy' })}
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-semibold text-quaternary">
                                    {t('training.detail.jobHistory.headers.status', { defaultValue: 'Status' })}
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-semibold text-quaternary">
                                    {t('training.detail.jobHistory.headers.confidence', { defaultValue: 'Confidence' })}
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-semibold text-quaternary">
                                    {t('training.detail.jobHistory.headers.duration', { defaultValue: 'Duration' })}
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-semibold text-quaternary">
                                    {t('training.detail.jobHistory.headers.date', { defaultValue: 'Date' })}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {safeJobs.map((job) => (
                                <tr key={job.job_id} className="group">
                                    <td colSpan={7} className="p-0">
                                        {/* Job Row */}
                                        <div
                                            className="flex items-center cursor-pointer hover:bg-secondary/50 border-b border-secondary"
                                            onClick={() => handleRowClick(job)}
                                        >
                                            <div className="py-3 px-3 w-10">
                                                <button className="p-1 hover:bg-secondary rounded">
                                                    {expandedJobId === job.job_id ? (
                                                        <ChevronDown className="size-4 text-tertiary" />
                                                    ) : (
                                                        <ChevronRight className="size-4 text-tertiary" />
                                                    )}
                                                </button>
                                            </div>
                                            <div className="py-3 px-3 flex-1">
                                                {getJobTypeBadge(job.job_type)}
                                            </div>
                                            <div className="py-3 px-3 flex-1">
                                                <span className="text-sm text-primary">
                                                    {job.strategy || '-'}
                                                </span>
                                            </div>
                                            <div className="py-3 px-3 flex-1">
                                                {getStatusBadge(job.status)}
                                            </div>
                                            <div className="py-3 px-3 flex-1">
                                                {job.confidence !== null && job.confidence !== undefined ? (
                                                    <span className="text-sm text-primary font-medium">
                                                        {(job.confidence * 100).toFixed(0)}%
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-tertiary">-</span>
                                                )}
                                            </div>
                                            <div className="py-3 px-3 flex-1">
                                                <span className="text-sm text-tertiary">
                                                    {formatDuration(job.duration_ms)}
                                                </span>
                                            </div>
                                            <div className="py-3 px-3 flex-1">
                                                <span className="text-sm text-tertiary">
                                                    {formatDate(job.created_at)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Expanded Job Details */}
                                        {expandedJobId === job.job_id && (
                                            <div className="px-3 pb-3 bg-secondary/30">
                                                {/* Job Metadata */}
                                                <div className="p-4 bg-secondary rounded-lg space-y-4 mt-3">
                                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                                        {job.model_version && (
                                                            <div>
                                                                <label className="text-xs font-medium text-tertiary">
                                                                    {t('training.detail.jobHistory.details.model', { defaultValue: 'Model' })}
                                                                </label>
                                                                <p className="text-sm text-primary font-mono">{job.model_version}</p>
                                                            </div>
                                                        )}
                                                        {job.tokens_used !== null && job.tokens_used !== undefined && (
                                                            <div>
                                                                <label className="text-xs font-medium text-tertiary">
                                                                    {t('training.detail.jobHistory.details.tokens', { defaultValue: 'Tokens' })}
                                                                </label>
                                                                <p className="text-sm text-primary">{job.tokens_used.toLocaleString()}</p>
                                                            </div>
                                                        )}
                                                        {job.cost_usd !== null && job.cost_usd !== undefined && (
                                                            <div>
                                                                <label className="text-xs font-medium text-tertiary">
                                                                    {t('training.detail.jobHistory.details.cost', { defaultValue: 'Cost' })}
                                                                </label>
                                                                <p className="text-sm text-primary">${job.cost_usd.toFixed(4)}</p>
                                                            </div>
                                                        )}
                                                        {job.completed_at && (
                                                            <div>
                                                                <label className="text-xs font-medium text-tertiary">
                                                                    {t('training.detail.jobHistory.details.completedAt', { defaultValue: 'Completed At' })}
                                                                </label>
                                                                <p className="text-sm text-primary">{formatDate(job.completed_at)}</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {job.error_message && (
                                                        <div>
                                                            <label className="text-xs font-medium text-error">
                                                                {t('training.detail.jobHistory.details.error', { defaultValue: 'Error' })}
                                                            </label>
                                                            <p className="text-sm text-error bg-error/10 p-2 rounded mt-1">
                                                                {job.error_message}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Extraction/Parsing Job: Show Extracted Fields (Collapsible Groups) */}
                                                {(job.job_type === 'parsing' || job.job_type === 'extraction') && job.status === 'completed' && (
                                                    <div className="mt-4">
                                                        <h4 className="text-sm font-semibold text-primary mb-3">
                                                            {t('training.detail.extraction.extractedFields', { defaultValue: 'Extracted Fields' })}
                                                        </h4>
                                                        <GroupedFieldsView
                                                            trainingDocId={trainingDocId}
                                                            parsingId={job.parsing_id || job.job_id}
                                                        />
                                                    </div>
                                                )}

                                                {/* Classification Job: Show Detected Type */}
                                                {job.job_type === 'classification' && job.status === 'completed' && (
                                                    <div className="mt-4 p-4 bg-secondary rounded-lg">
                                                        <h4 className="text-sm font-semibold text-primary mb-3">
                                                            {t('training.detail.classification.result', { defaultValue: 'Classification Result' })}
                                                        </h4>

                                                        {/* Multi-document detection - show all types with page ranges */}
                                                        {job.is_multi_document && job.document_types_detected && job.document_types_detected.length > 0 ? (
                                                            <div className="space-y-3">
                                                                <div className="flex items-center gap-2">
                                                                    <Badge color="warning" size="sm">
                                                                        {t('training.detail.classification.multiDocument', {
                                                                            defaultValue: 'Multi-Document ({{count}} types)',
                                                                            count: job.document_types_detected.length
                                                                        })}
                                                                    </Badge>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    {job.document_types_detected.map((docType, idx) => {
                                                                        const pageRange = docType.page_start === docType.page_end
                                                                            ? `Page ${docType.page_start}`
                                                                            : `Pages ${docType.page_start}-${docType.page_end}`;
                                                                        const confidenceColor = docType.confidence >= 0.9 ? 'success'
                                                                            : docType.confidence >= 0.7 ? 'warning' : 'error';

                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                className="flex items-center justify-between p-2 bg-tertiary/30 rounded"
                                                                            >
                                                                                <div className="flex items-center gap-3">
                                                                                    <span className="text-xs text-tertiary font-mono">
                                                                                        {pageRange}
                                                                                    </span>
                                                                                    <span className="text-sm text-primary font-medium">
                                                                                        {docType.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                                                    </span>
                                                                                </div>
                                                                                <Badge color={confidenceColor} size="sm">
                                                                                    {(docType.confidence * 100).toFixed(0)}%
                                                                                </Badge>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            /* Single document type - original display */
                                                            <div className="flex items-center gap-4">
                                                                <div>
                                                                    <label className="text-xs font-medium text-tertiary">
                                                                        {t('training.detail.classification.detectedType', { defaultValue: 'Detected Type' })}
                                                                    </label>
                                                                    <p className="text-sm text-primary font-medium">
                                                                        {job.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}
                                                                    </p>
                                                                </div>
                                                                {job.confidence !== null && job.confidence !== undefined && (
                                                                    <div>
                                                                        <label className="text-xs font-medium text-tertiary">
                                                                            {t('training.detail.jobHistory.headers.confidence', { defaultValue: 'Confidence' })}
                                                                        </label>
                                                                        <Badge
                                                                            color={job.confidence >= 0.9 ? 'success' : job.confidence >= 0.7 ? 'warning' : 'error'}
                                                                            size="sm"
                                                                        >
                                                                            {(job.confidence * 100).toFixed(1)}%
                                                                        </Badge>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {/* TODO: Add document type change and validation buttons */}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
